import React from "react";
import emblem from "../assets/logo-emblem-dark.png";

/**
 * LogoLoader — branded loading indicator.
 *
 * Two stacked copies of the Mazad AD emblem: the base copy is
 * greyscaled + dim ("empty" state) and the overlay is full-colour +
 * clip-path animated so the piece progressively fills from bottom up.
 * The motion + colour reveal are handled entirely in CSS
 * (`.mad-loader*` classes in `index.css`) so this component is a
 * dumb wrapper — no state, no timers.
 *
 * Props:
 *   size    — pixel size of the emblem square. Defaults to 96.
 *   label   — accessible label (visually hidden). Defaults to "Loading".
 *   fullscreen — if true, renders inside a min-h-[60vh] centered wrapper
 *                so any page can drop it in as its whole-body loader.
 *   className — optional wrapper class overrides.
 */
export default function LogoLoader({ size = 96, label = "Loading", fullscreen = false, className = "" }) {
  const emblemEl = (
    <span className="mad-loader" role="status" aria-live="polite" aria-label={label}>
      <img
        src={emblem}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className="mad-loader__img mad-loader__base object-contain"
        style={{ width: size, height: size }}
      />
      <img
        src={emblem}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        className="mad-loader__img mad-loader__fill object-contain"
        style={{ width: size, height: size }}
      />
      <span className="sr-only">{label}</span>
    </span>
  );

  if (!fullscreen) {
    return <span className={className}>{emblemEl}</span>;
  }

  return (
    <div
      className={`w-full min-h-[60vh] flex items-center justify-center ${className}`}
      data-testid="logo-loader"
    >
      {emblemEl}
    </div>
  );
}
